/**
 * Read an account sign-in link's token (`?signin=…`) from the launch URL and
 * strip it from the address bar, so a refresh can't replay it. Call once at
 * startup.
 */
export function readLaunchSignInToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const u = new URL(window.location.href);
    const token = u.searchParams.get('signin');
    if (!token) return null;
    u.searchParams.delete('signin');
    const qs = u.searchParams.toString();
    window.history.replaceState({}, '', u.pathname + (qs ? `?${qs}` : ''));
    return token;
  } catch {
    return null;
  }
}

/** Set across the reload that follows signing in on a new device, so the
 *  game can say "welcome back" once it has restarted as the account. */
export const SIGNED_IN_KEY = 'goodtime.signedIn';

/** What this device keeps about the player it plays as. Dropped when it
 *  starts playing as an account's player, so the account's own copy loads. */
export const PLAYER_CACHE_KEYS = [
  'goodtime.profile',
  'goodtime.playerName',
  'goodtime.playerAvatarId',
  'goodtime.playerAvatarCustom',
] as const;

/** Set just before the restart that signs this device in as another
 *  player; the next start drops the old player's copies. */
export const ADOPTED_KEY = 'goodtime.adoptedPlayer';

/**
 * Call at startup, before the profile is read: after signing in as another
 * player, forget what this device kept about the old one. Done on the next
 * start rather than before the restart, because the old page can still
 * save its profile while it unloads, and that copy must never be pushed
 * over the account's.
 */
export function forgetOldPlayerAfterSignIn(): void {
  try {
    if (!localStorage.getItem(ADOPTED_KEY)) return;
    for (const key of PLAYER_CACHE_KEYS) localStorage.removeItem(key);
    localStorage.removeItem(ADOPTED_KEY);
  } catch {
    /* private mode */
  }
}
